import Joi from "joi";
import { getCartCount } from "../api/cart.api";
import moment from "moment-timezone";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

export const formatWithDollar = (value: string) => {
  return /^\d+$/.test(value) ? `$${value}` : value;
};

export const formatTitleCase = (str: string) => {
  if (!str) return "-";
  return str
    .replace(/_/g, " ") // Replace underscores with spaces
    .toLowerCase() // Convert entire string to lowercase
    .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word
};

export const splitPhoneNumber = (
  formatSentFromDB: string | null | undefined
) => {
  if (!formatSentFromDB) {
    return {
      countryCode: "+1", // Default country code
      phoneNumber: "",
    };
  }

  const contactNumberRegex = /^(\+\d{1,4})[-\s]?(\d{6,15})$/;
  const match = formatSentFromDB.match(contactNumberRegex);

  return match
    ? {
        countryCode: match[1], // Country code (e.g., +1)
        phoneNumber: match[2], // Phone number without country code
      }
    : {
        countryCode: "", // Empty if invalid format
        phoneNumber: "",
      };
};

export const concatenatePhoneNumber = (phoneNumberFormatValues: any) => {
  const { countryCode, phoneNumber } = phoneNumberFormatValues;
  if (!countryCode && !phoneNumber) {
    return "";
  }
  return `${countryCode}-${phoneNumber}`;
};

export const validateImageDimensions = (
  file: any,
  maxWidth: number,
  maxHeight: number
): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const { width, height } = img;
      resolve(width <= maxWidth && height <= maxHeight);
    };
    img.onerror = () => {
      resolve(false);
    };
    img.src = URL.createObjectURL(file);
  });
};
export const formatDateTime = (dateString: string, day?: boolean): string => {
  const date = new Date(dateString);

  if (day) {
    const weekday = date.toLocaleDateString("en-US", { weekday: "short" });
    const dayNum = date.getDate();
    const month = date.toLocaleDateString("en-US", { month: "short" });
    const year = date.getFullYear();

    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    return `${weekday}, ${dayNum} ${month} ${year} ${formattedTime}`;
  }

  const formattedDate = date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const formattedTime = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return `${formattedDate} ${formattedTime}`;
};
export const isOnlyCountryCode = (
  value: string | undefined | null
): boolean => {
  if (!value) return false;

  const formattedValue = value.trim();
  // Check if the value is just a country code (e.g., +1 or +91)
  return /^(\+\d{1,3})$/.test(formattedValue);
};

export const extractCountryAndNumber = (number: any) => {
  if (!number) return null;
  const match = number.trim().match(/^(\+\d{1,4})\s*(.*)$/);

  if (!match) return null;

  return {
    countryCode: match[1],
    number: match[2].replace(/[-]/g, ""), // remove dashes
  };
};

export const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date); // MM/DD/YYYY
};

// src/utils/get-current-tenant-id.ts

export const getCurrentTenantId = (): number | null => {
  try {
    const loginData = localStorage.getItem("loginData");
    if (!loginData) return null;

    const parsedData = JSON.parse(loginData);
    const id = parsedData?.tenantDetails?.id;

    // return typeof id === "number" ? id : (Number.isNaN(Number(id)) ? null : Number(id));
    return typeof id === "number" ? id : Number(id) || null;
  } catch (error) {
    console.error("Failed to parse loginData from localStorage:", error);
    return null;
  }
};

export const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good Morning";
  if (hour < 17) return "Good Afternoon";
  return "Good Evening";
};

export const truncateWords = (text: string, wordLimit: number) => {
  const words = text.split(" ");
  if (words.length <= wordLimit) return text;
  return words.slice(0, wordLimit).join(" ") + "...";
};

export const fetchCartCount = async () => {
  try {
    const response = await getCartCount();
    const count = response.data?.data?.data?.totalCount;
    return count;
  } catch (error) {
    console.error("ERROR IN FETCHING CART COUNT::", error);
  }
};

export const maskPhoneNumber = (phone: string, countryCode = "+1") => {
  if (!phone) return "";

  const digits = phone.replace(/\D/g, "");

  if (digits.length < 2) {
    return `${countryCode} ${digits}`;
  }

  const firstDigit = digits[0];
  const masked = "X".repeat(digits.length - 1);

  return `${countryCode} ${firstDigit}${masked}`;
};

export const formatTransactionDate = (d: any) => {
  const date = new Date(d);
  const datePart = date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const timePart = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return `${datePart} at ${timePart}`;
};

export const validateEmail = (value: string) => {
  const regex = /^[A-Za-z0-9._%+-]+@(?:[A-Za-z0-9-]+\.)+[A-Za-z]{2,}$/;
  return regex.test(value);
};

export const convertUTCToLocal = (
  utcDateString: string,
  format: string = "YYYY-MM-DD hh:mm:ss A"
) => {
  return moment.utc(utcDateString).tz(moment.tz.guess()).format(format);
};

export const joiEmailSchema = Joi.string().email({
  tlds: { allow: true },
  allowUnicode: false,
  minDomainSegments: 2,
});

// remove country code & return 10 digits ph num
export const normalizePhoneNumber = (phone = "") => {
  const digitsOnly = phone.replace(/\D/g, "");
  return digitsOnly.slice(-10);
};
export const extractCountryCodeFromPhone = (phone = "") => {
  const digitsOnly = phone.replace(/\D/g, "");
  // Remove the last 10 digits (phone number), return the rest as country code
  return digitsOnly.length > 10 ? `+${digitsOnly.slice(0, -10)}` : "+1"; // Default to +1 if no country code found
};